import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormData } from '../../types/form';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import DatePicker from '../ui/DatePicker';
import Disclaimer from '../ui/Disclaimer';
import { validateBirthDate } from '../../utils/validation';
import { useHubspot } from '../../hooks/useHubspot';
import DisclaimerContent from '../../utils/disclaimerContent';
import { useFirstStepDisclaimer } from '../../hooks/useFirstStepDisclaimer';

type BirthDateFormProps = {
  formData: FormData;
  onSubmit: (data: Partial<FormData>) => void;
};

export default function BirthDateForm({ formData, onSubmit }: BirthDateFormProps) {
  const { t } = useTranslation('wizard');
  const [error, setError] = useState<string | null>(null);
  const [localDate, setLocalDate] = useState(formData.birthDate);
  const hubspot = useHubspot();
  const { shouldShow: showDisclaimer } = useFirstStepDisclaimer();

  const handleDateChange = (value: string) => {
    const dateError = validateBirthDate(value);
    setError(dateError);
    setLocalDate(value);
  };

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!localDate) {
      setError(t('birthDate.requiredError'));
      return;
    }

    const dateError = validateBirthDate(localDate);
    if (dateError) {
      setError(dateError);
      return;
    }

    // Track birth date in HubSpot
    hubspot.identify({
      email: formData.email,
      date_of_birth: localDate,
      date_of_birth__c: localDate,
      sensitive_date_of_birth: localDate
    });

    // Track form step completion
    hubspot.trackEvent('birth_date_step_completed', {
      email: formData.email,
      form_step: 'birthdate',
      date_of_birth: localDate,
      date_of_birth__c: localDate,
      sensitive_date_of_birth: localDate
    });

    onSubmit({ birthDate: localDate });
  };

  return (
    <form id="birth-date-form" name="birth-date-form" onSubmit={handleContinue} className="space-y-6">
      <div className="text-center">
        <Title 
          id="birth-date-title"
          name="birth-date-title"
          as="h1" 
          className="mb-2"
        >
          {t('birthDate.title')}
        </Title>
        <Description 
          id="birth-date-description"
          name="birth-date-description"
          size="lg"
        >
          {t('birthDate.description')}
        </Description>
      </div>

      <DatePicker
        id="birth-date-picker"
        name="birth-date-picker"
        value={localDate}
        onChange={handleDateChange}
        error={error}
        defaultOption="empty"
      />

      {/* Hidden fields */}
      <input 
        type="hidden" 
        name="email" 
        value={formData.email} 
        data-hs-field-hidden="true"
      />
      <input 
        type="hidden" 
        name="date_of_birth" 
        value={localDate} 
        data-hs-field-hidden="true"
      />
      <input 
        type="hidden" 
        name="date_of_birth__c" 
        value={localDate} 
        data-hs-field-hidden="true"
      />
      <input 
        type="hidden" 
        name="sensitive_date_of_birth" 
        value={localDate} 
        data-hs-field-hidden="true"
      />

      <Button
        id="birth-date-submit"
        name="birth-date-submit"
        type="submit"
        fullWidth
        size="lg"
        disabled={!localDate || !!error}
      >
        {t('birthDate.submit')}
      </Button>

      {showDisclaimer && (
        <Disclaimer
          id="birth-date-general-disclaimer"
          name="birth-date-general-disclaimer"
        >
          <DisclaimerContent />
        </Disclaimer>
      )}
    </form>
  );
}